import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxImageCompressService } from 'ngx-image-compress';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { AuthService } from '../../auth.service';
import { PhotoService } from '../photo.service';
import { LoaderService } from '../../loader.service';

import { HeaderComponent } from '../../header/header.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { ProgressBarComponent } from '../../shared/progress-bar/progress-bar.component';

import { User } from '../../shared/interfaces/user';

@Component({
  selector: 'app-upload-photo',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, LoaderComponent, ProgressBarComponent],
  providers: [NgxImageCompressService],
  templateUrl: './upload-photo.component.html',
  styleUrl: './upload-photo.component.scss'
})
export class UploadPhotoComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() selector = { value: 0, name: 'account' }
  @Input() flow!: string
  
  @ViewChild('fileUpload') fileUpload!: ElementRef
  @ViewChild('photoContainer') photoContainer!: ElementRef

  loading$ = this.loaderService.loading$
  menu = ['account', 'photos', 'contacts', 'legal']
  fileList: FileList | null = null
  showCropper!: { 
    data: File,
    temp?: string,
    size: { height: number; width: number; }
  } | null
  tempCropperPhotoInBase64!: string
  user!: User | null
  firstname = ''
  userId!: number
  hideSaveButton = false
  photoUrl!: string | undefined
  showHelp = false
  helpIsOpened = false
  subscriptionInProgress = false
  hightlightedSteps: {
    first: boolean, 
    second?: boolean, 
    third?: boolean, 
    fourth?: boolean
  } = { first: true, second: true, third: true, fourth: true }
  photoContainerDimensions!: {width: number, height: number}
  private readonly _destroyed = new Subject<void>()

  constructor( 
    private authService: AuthService,
    private imageCompress: NgxImageCompressService,
    public router: Router,
    private photoService: PhotoService,
    private route: ActivatedRoute,
    private loaderService: LoaderService
  ){
    this.authService.authMe$.subscribe(res => {
      this.user = res
    })

    this.firstname = this.authService.get('firstname')
  }

  ngOnInit(){
    console.log('upload photo')
    this.photoService.photoToCropInBase64 = null

    this.loaderService.show()

    this.photoService.cropperState().pipe(takeUntil(this._destroyed)).subscribe((res) => {
      this.showCropper = res
    })

    // reset fileList when uploaded photo is discarded
    this.photoService.fileList.subscribe(res => {
      this.fileList = res
      this.hideSaveButton = false
    })

    if(this.authService.get('user')){
      let userStoredLocally = JSON.parse(this.authService.get('user'))
      if(userStoredLocally) this.userId = userStoredLocally.id

      this.authService.getProfile(this.userId).subscribe(res => {
        this.user = res
        userStoredLocally = res
        this.authService.remove('user')
        this.authService.set('user', JSON.stringify(res))

        // make sure the user does not upload more than one photo
        if(userStoredLocally.main_photo) this.router.navigate(['/home'])
      })
    }

    this.route.url.pipe(takeUntil(this._destroyed)).subscribe(res => {
      if(res[0].path === 'subscribe-step-four'){
        this.subscriptionInProgress = true
      }
    })

    // redirect to /upload-photo when on /upload-photo/crop and no image was uploaded (back from /photo situation)
    if(!this.photoService.photoToCropInBase64) this.router.navigate(['/upload-photo'])
  }

  ngAfterViewChecked(){
    if(!this.photoContainer.nativeElement) return
      
    this.photoContainer.nativeElement.getBoundingClientRect()

    this.loaderService.hide()
  }

  openFileInput(){
    this.fileUpload.nativeElement.click()
  }

  onPhotoInputChange(event: Event){
    console.log('onPhotoInputChange')
    const element = event.currentTarget as HTMLInputElement
    this.fileList = element.files

    if(element.files && element.files.length) {
      const file = element.files[0] // File
      this.photoService.photoFile = file
      const reader = new FileReader()

      reader.onload = () => {
        const img = new Image()
   
        img.onload = () => {
          this.tempCropperPhotoInBase64 = img.src

          this.showCropper = { 
            data: file,
            temp: this.tempCropperPhotoInBase64,
            size: { height: 340, width: 340 }
          }

          this.photoService.showCropper = this.showCropper
          this.photoService.photoToCropInBase64 = this.tempCropperPhotoInBase64
          
          if(this.router.url.includes('subscribe')){
            this.router.navigate(['/subscribe-step-four/crop'])
          } else {
            this.router.navigate(['/upload-photo/crop'])
          }

          this.hideSaveButton = true
        }
       
        img.src = reader.result as string
      }

      reader.readAsDataURL(file)
      
      // reset the event
      element.value = ''
    }
  }

  discardPhoto(){
    this.fileList = null
    this.photoService.cropper();
    this.showCropper = null;
  }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
  }
}
